<template>
  <div class="p-4">
    <div class="flex align-items-center justify-content-between mb-4">
      <h2 class="m-0">Panel de Superusuario</h2>
      <Tag v-if="userStore.isSuperUser" severity="danger" value="SUPERUSUARIO" />
    </div>

    <!-- El panel completo es exclusivo de superusuario -->
    <div v-if="!userStore.isSuperUser" class="card">
      <p class="m-0 text-500">Esta sección solo está disponible para superusuarios.</p>
    </div>

    <template v-if="userStore.isSuperUser">
      <!-- Selector de congregación: al elegirla se muestran sus usuarios y su modelo de permisos -->
      <div class="card mb-4">
        <div class="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h3 class="m-0">Congregación</h3>
          <Button icon="pi pi-plus" label="Nueva congregación" size="small" @click="showCreateCong = true" />
        </div>

        <Select
          v-model="selectedCongId"
          :options="congregations"
          option-label="name"
          option-value="id"
          filter
          filter-placeholder="Buscar congregación…"
          :loading="loadingCongs"
          showClear
          placeholder="Seleccionar una congregación"
          class="w-full md:w-25rem"
        />

        <div v-if="selectedCong" class="mt-4">
          <div class="cong-detail-grid mb-4">
            <div>
              <div class="text-500 text-sm mb-1">Miembros</div>
              <div class="font-medium">{{ selectedCong.memberCount }}</div>
            </div>
            <div>
              <div class="text-500 text-sm mb-1">Creada</div>
              <div class="font-medium">{{ formatDate(selectedCong.created_at) }}</div>
            </div>
            <div>
              <div class="text-500 text-sm mb-1">Modelo de permisos</div>
              <Select
                :modelValue="selectedCong.permissionTemplateId"
                @update:modelValue="(val) => changeCongregationTemplate(selectedCong, val)"
                :options="templateOptions"
                option-label="name"
                option-value="id"
                class="w-full"
                placeholder="Sin modelo"
              />
            </div>
          </div>

          <div class="flex align-items-center justify-content-between mb-2">
            <h4 class="m-0">Usuarios — {{ selectedCong.name }}</h4>
            <div class="flex gap-1">
              <Button icon="pi pi-plus" label="Agregar usuario" size="small" @click="showCreateUser = true" />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar congregación'"
                @click="confirmDeleteCong(selectedCong)"
              />
            </div>
          </div>

          <!-- Enlace reutilizable para que la propia congregación cree/gestione sus usuarios -->
          <div class="invite-row mb-3">
            <div class="flex-1 min-w-0">
              <div class="font-medium mb-1">Enlace de gestión de usuarios</div>
              <div v-if="manageInviteForSelectedCong" class="flex align-items-center gap-1">
                <span
                  class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                  style="max-width:340px"
                >{{ getPublicUrl(manageInviteForSelectedCong.token, 'manage_users') }}</span>
                <Button
                  :icon="copiedInviteToken === manageInviteForSelectedCong.token ? 'pi pi-check' : 'pi pi-copy'"
                  size="small"
                  text
                  :severity="copiedInviteToken === manageInviteForSelectedCong.token ? 'success' : 'secondary'"
                  v-tooltip="'Copiar enlace'"
                  @click="copyInviteLink(manageInviteForSelectedCong.token, 'manage_users')"
                />
              </div>
              <div v-else class="text-sm text-500">
                Compartilo con la congregación para que creen y administren sus propios usuarios.
              </div>
            </div>
            <Button
              v-if="!manageInviteForSelectedCong"
              icon="pi pi-link"
              label="Generar enlace"
              size="small"
              severity="secondary"
              :loading="creatingManageInvite"
              @click="generateManageInvite(selectedCong)"
            />
            <Button
              v-else
              icon="pi pi-trash"
              size="small"
              severity="danger"
              text
              v-tooltip="'Revocar enlace'"
              @click="confirmRevokeInvite(manageInviteForSelectedCong)"
            />
          </div>

          <DataTable :value="congUsers" :loading="loadingUsers" size="small" stripedRows>
            <Column field="email" header="Usuario" />
            <Column field="role" header="Rol" style="width: 150px">
              <template #body="{ data }">
                <Select
                  :modelValue="data.role"
                  @update:modelValue="(val) => updateUserRole(data, val)"
                  :options="roleOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </template>
            </Column>
            <Column header="Congregación" style="width: 200px">
              <template #body="{ data }">
                <Select
                  :modelValue="selectedCong.id"
                  @update:modelValue="(val) => moveUserToCongregation(data, val)"
                  :options="congregations"
                  option-label="name"
                  option-value="id"
                  class="w-full"
                />
              </template>
            </Column>
            <Column field="created_at" header="Creado" style="width: 140px">
              <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
            </Column>
            <Column header="" style="width: 110px">
              <template #body="{ data }">
                <div class="flex gap-1 justify-content-end">
                  <Button
                    icon="pi pi-key"
                    size="small"
                    severity="secondary"
                    text
                    v-tooltip="'Renovar contraseña'"
                    @click="openResetPassword(data)"
                  />
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    text
                    v-tooltip="'Eliminar usuario'"
                    @click="confirmDeleteUser(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <div v-else class="text-center py-4 text-500">
          Seleccioná una congregación para ver sus usuarios y su modelo de permisos.
        </div>
      </div>

      <!-- Resto de la administración, agrupado por pestañas -->
      <TabView>
        <TabPanel header="Todas las congregaciones">
          <DataTable :value="congregations" :loading="loadingCongs" size="small" stripedRows>
            <Column field="name" header="Nombre" />
            <Column header="Modelo de permisos" style="width: 220px">
              <template #body="{ data }">
                <Select
                  :modelValue="data.permissionTemplateId"
                  @update:modelValue="(val) => changeCongregationTemplate(data, val)"
                  :options="templateOptions"
                  option-label="name"
                  option-value="id"
                  class="w-full"
                  placeholder="Sin modelo"
                />
              </template>
            </Column>
            <Column field="memberCount" header="Miembros" style="width: 90px; text-align:center" />
            <Column field="created_at" header="Creada" style="width: 140px">
              <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
            </Column>
            <Column header="" style="width: 120px">
              <template #body="{ data }">
                <div class="flex gap-1 justify-content-end">
                  <Button
                    icon="pi pi-eye"
                    size="small"
                    severity="secondary"
                    text
                    v-tooltip="'Ver detalle'"
                    @click="selectedCongId = data.id"
                  />
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    text
                    v-tooltip="'Eliminar'"
                    @click="confirmDeleteCong(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <TabPanel header="Modelos de permisos">
          <div class="flex justify-content-end mb-3">
            <Button icon="pi pi-plus" label="Nuevo modelo" size="small" @click="openCreateTemplate" />
          </div>

          <div v-if="loadingTemplates" class="text-center py-3 text-500">Cargando…</div>
          <div v-else-if="!templates.length" class="text-center py-3 text-500">Sin modelos de permisos.</div>
          <div v-else class="flex flex-column gap-2">
            <div v-for="tpl in templates" :key="tpl.id" class="invite-row">
              <div class="flex-1 min-w-0">
                <div class="flex align-items-center gap-2">
                  <span class="font-medium">{{ tpl.name }}</span>
                  <Tag v-if="tpl.isDefault" severity="info" value="Predeterminado" />
                </div>
                <div class="text-sm text-500 mt-1">
                  {{ tpl.disabledPages.length }} de {{ MENU_PAGES.length }} páginas deshabilitadas
                </div>
              </div>
              <div class="flex gap-1">
                <Button
                  v-if="!tpl.isDefault"
                  icon="pi pi-star"
                  size="small"
                  severity="secondary"
                  text
                  v-tooltip="'Marcar como predeterminado'"
                  @click="setDefaultTemplate(tpl)"
                />
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  text
                  v-tooltip="'Editar'"
                  @click="openEditTemplate(tpl)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  text
                  v-tooltip="'Eliminar'"
                  @click="confirmDeleteTemplate(tpl)"
                />
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Enlaces de invitación">
          <div class="flex justify-content-end mb-3">
            <Button icon="pi pi-plus" label="Nuevo enlace" size="small" @click="showCreateInvite = true" />
          </div>

          <div v-if="loadingInvites" class="text-center py-3 text-500">Cargando…</div>
          <div v-else-if="!invites.length" class="text-center py-3 text-500">Sin enlaces de invitación.</div>
          <div v-else class="flex flex-column gap-2">
            <div v-for="inv in invites" :key="inv.token" class="invite-row">
              <div class="flex-1 min-w-0">
                <div class="flex align-items-center gap-2 mb-1">
                  <span class="font-medium">{{ inv.name || 'Enlace sin nombre' }}</span>
                  <Tag
                    :severity="inv.type === 'manage_users' ? 'info' : 'secondary'"
                    :value="inv.type === 'manage_users' ? 'Gestión de usuarios' : 'Crea congregación'"
                  />
                  <Tag v-if="inv.type !== 'manage_users'" :severity="inv.used ? 'secondary' : 'success'" :value="inv.used ? 'Usado' : 'Activo'" />
                </div>
                <div v-if="inv.type === 'manage_users'" class="flex align-items-center gap-1">
                  <span class="text-sm text-500 mr-1">{{ inv.congregationName }} —</span>
                  <span
                    class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                    style="max-width:260px"
                  >{{ getPublicUrl(inv.token, 'manage_users') }}</span>
                  <Button
                    :icon="copiedInviteToken === inv.token ? 'pi pi-check' : 'pi pi-copy'"
                    size="small"
                    text
                    :severity="copiedInviteToken === inv.token ? 'success' : 'secondary'"
                    v-tooltip="'Copiar enlace'"
                    @click="copyInviteLink(inv.token, 'manage_users')"
                  />
                </div>
                <div v-else-if="inv.used" class="text-sm text-500">Congregación creada: {{ inv.congregationName || '—' }}</div>
                <div v-else class="flex align-items-center gap-1">
                  <span
                    class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                    style="max-width:340px"
                  >{{ getPublicUrl(inv.token) }}</span>
                  <Button
                    :icon="copiedInviteToken === inv.token ? 'pi pi-check' : 'pi pi-copy'"
                    size="small"
                    text
                    :severity="copiedInviteToken === inv.token ? 'success' : 'secondary'"
                    v-tooltip="'Copiar enlace'"
                    @click="copyInviteLink(inv.token)"
                  />
                </div>
              </div>
              <Button
                v-if="!inv.used"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Revocar'"
                @click="confirmRevokeInvite(inv)"
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Sitio">
          <SiteMetadataAdmin />
          <LinkMetadataAdmin class="mt-4" />
        </TabPanel>
      </TabView>
    </template>

    <!-- Dialog: crear congregación -->
    <Dialog v-model:visible="showCreateCong" header="Nueva Congregación" modal style="width: 400px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Nombre de la congregación</label>
          <InputText v-model="newCongName" placeholder="Ej: Congregación Norte" autofocus />
        </div>
        <div v-if="errorCong" class="text-red-500 text-sm">{{ errorCong }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreateCong = false" />
          <Button label="Crear" :loading="savingCong" @click="createCongregation" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: crear usuario -->
    <Dialog v-model:visible="showCreateUser" header="Agregar Usuario" modal style="width: 420px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Usuario</label>
          <InputText v-model="newUser.email" placeholder="usuario" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Contraseña</label>
          <div class="flex gap-2">
            <InputText v-model="newUser.password" placeholder="Contraseña inicial" class="flex-1" />
            <Button icon="pi pi-refresh" severity="secondary" v-tooltip="'Generar aleatoria'" @click="newUser.password = generatePassword()" />
            <Button icon="pi pi-copy" severity="secondary" v-tooltip="'Copiar'" :disabled="!newUser.password" @click="copyNewUserPassword" />
          </div>
        </div>
        <div class="flex flex-column gap-1">
          <label>Rol</label>
          <Select
            v-model="newUser.role"
            :options="roleOptions"
            option-label="label"
            option-value="value"
            placeholder="Seleccionar rol"
          />
        </div>
        <div v-if="errorUser" class="text-red-500 text-sm">{{ errorUser }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreateUser = false" />
          <Button label="Crear" :loading="savingUser" @click="createUser" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: renovar contraseña -->
    <Dialog v-model:visible="showResetPassword" header="Renovar contraseña" modal style="width: 420px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="text-sm text-500">
          Usuario: <span class="font-medium">{{ resetPasswordUser?.email }}</span>
        </div>
        <div class="flex flex-column gap-1">
          <label>Nueva contraseña</label>
          <div class="flex gap-2">
            <InputText v-model="resetPasswordValue" placeholder="Nueva contraseña" class="flex-1" />
            <Button icon="pi pi-refresh" severity="secondary" v-tooltip="'Generar aleatoria'" @click="generateRandomPassword" />
          </div>
        </div>
        <div v-if="errorResetPassword" class="text-red-500 text-sm">{{ errorResetPassword }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showResetPassword = false" />
          <Button label="Guardar" :loading="savingResetPassword" @click="submitResetPassword" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: nuevo enlace de invitación -->
    <Dialog v-model:visible="showCreateInvite" header="Nuevo enlace de invitación" modal style="width: 400px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Nombre (opcional)</label>
          <InputText
            v-model="newInviteName"
            placeholder="Ej: Congregación Norte"
            autofocus
            @keyup.enter="doCreateInvite"
          />
        </div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreateInvite = false" />
          <Button label="Crear" :loading="creatingInvite" @click="doCreateInvite" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: crear/editar modelo de permisos -->
    <Dialog
      v-model:visible="showTemplateDialog"
      :header="editingTemplate ? 'Editar modelo de permisos' : 'Nuevo modelo de permisos'"
      modal
      style="width: 480px"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Nombre</label>
          <InputText v-model="templateForm.name" placeholder="Ej: Publicador básico" autofocus />
        </div>
        <div class="flex flex-column gap-1">
          <label>Menú habilitado</label>
          <div class="menu-config-grid">
            <div v-for="page in MENU_PAGES" :key="page.key" class="flex align-items-center gap-2">
              <Checkbox
                :inputId="`tpl-menu-${page.key}`"
                :modelValue="!templateForm.disabledPages.includes(page.key)"
                binary
                @update:modelValue="(val) => toggleTemplatePage(page.key, val)"
              />
              <label :for="`tpl-menu-${page.key}`" class="cursor-pointer flex align-items-center gap-1">
                <i :class="page.icon" class="text-500" /> {{ page.label }}
              </label>
            </div>
          </div>
        </div>
        <div v-if="errorTemplate" class="text-red-500 text-sm">{{ errorTemplate }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showTemplateDialog = false" />
          <Button label="Guardar" :loading="savingTemplate" @click="saveTemplate" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useApi } from '@/composables/useApi'
import { useUserStore } from '@/store/useUserStore'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/composables/useAppToast'
import { generatePassword } from '@/composables/usePasswordGenerator'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import Select from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'
import ConfirmDialog from 'primevue/confirmdialog'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import SiteMetadataAdmin from '@/components/SiteMetadataAdmin.vue'
import LinkMetadataAdmin from '@/components/LinkMetadataAdmin.vue'
import { MENU_PAGES } from '@/constants/menuPages'
import { useCongregationInvites } from '@/composables/useCongregationInvites'

const api = useApi()
const userStore = useUserStore()
const confirm = useConfirm()
const toast = useAppToast()
const { getPublicUrl, getInvites, createInvite, revokeInvite } = useCongregationInvites()

const congregations = ref([])
const loadingCongs = ref(false)
const showCreateCong = ref(false)
const newCongName = ref('')
const savingCong = ref(false)
const errorCong = ref(null)

const selectedCong = ref(null)
const congUsers = ref([])
const loadingUsers = ref(false)
const showCreateUser = ref(false)
const savingUser = ref(false)
const errorUser = ref(null)
const newUser = ref({ email: '', password: '', role: 'member' })

const showResetPassword = ref(false)
const resetPasswordUser = ref(null)
const resetPasswordValue = ref('')
const savingResetPassword = ref(false)
const errorResetPassword = ref(null)

const roleOptions = [
  { label: 'Miembro', value: 'member' },
  { label: 'Administrador', value: 'admin' },
]

// Selector filtrable de congregación: al elegir una, se cargan sus usuarios abajo.
const selectedCongId = computed({
  get: () => selectedCong.value?.id ?? null,
  set: (id) => {
    if (!id) { selectedCong.value = null; return }
    const cong = congregations.value.find((c) => c.id === id)
    if (cong) openCongUsers(cong)
  },
})

// ── Modelos de permisos ────────────────────────────────────────────────────────
const templates = ref([])
const loadingTemplates = ref(false)
const showTemplateDialog = ref(false)
const editingTemplate = ref(null)
const templateForm = ref({ name: '', disabledPages: [] })
const savingTemplate = ref(false)
const errorTemplate = ref(null)
const templateOptions = computed(() => [
  { id: null, name: 'Sin modelo (todo habilitado)' },
  ...templates.value,
])

// ── Enlaces de invitación ─────────────────────────────────────────────────────
const invites = ref([])
const loadingInvites = ref(false)
const showCreateInvite = ref(false)
const newInviteName = ref('')
const creatingInvite = ref(false)
const copiedInviteToken = ref(null)
const creatingManageInvite = ref(false)

// Enlace reutilizable de gestión de usuarios de la congregación que está seleccionada.
const manageInviteForSelectedCong = computed(() => {
  if (!selectedCong.value) return null
  return invites.value.find(
    (i) => i.type === 'manage_users' && i.congregationId === selectedCong.value.id
  ) || null
})

const formatDate = (dt) => {
  if (!dt) return '-'
  return new Date(dt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const loadCongregations = async () => {
  loadingCongs.value = true
  try {
    const result = await api.get('/api/admin/congregations')
    congregations.value = result.congregations || []
  } finally {
    loadingCongs.value = false
  }
}

const createCongregation = async () => {
  if (!newCongName.value.trim()) { errorCong.value = 'El nombre es requerido'; return }
  savingCong.value = true
  errorCong.value = null
  try {
    const result = await api.post('/api/admin/congregations', { name: newCongName.value })
    if (result.error) { errorCong.value = result.error; return }
    showCreateCong.value = false
    newCongName.value = ''
    await loadCongregations()
  } finally {
    savingCong.value = false
  }
}

const confirmDeleteCong = (cong) => {
  confirm.require({
    message: `¿Eliminar "${cong.name}"? Los usuarios quedarán sin congregación.`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await api.del(`/api/admin/congregations/${cong.id}`)
      if (selectedCong.value?.id === cong.id) selectedCong.value = null
      await loadCongregations()
    },
  })
}

const openCongUsers = async (cong) => {
  selectedCong.value = cong
  newUser.value = { email: '', password: '', role: 'member' }
  loadingUsers.value = true
  try {
    const result = await api.get(`/api/admin/congregations/${cong.id}/users`)
    congUsers.value = result.users || []
  } finally {
    loadingUsers.value = false
  }
}

const updateUserRole = async (user, role) => {
  if (!role || role === user.role) return
  const previous = user.role
  user.role = role
  const result = await api.put(`/api/admin/users/${user.uid}`, { role })
  if (result?.error) {
    user.role = previous
    toast.error('No se pudo cambiar el rol', result.error)
    return
  }
  toast.success('Rol actualizado', `${user.email} ahora es ${role === 'admin' ? 'administrador' : 'miembro'}.`)
}

// Mover a otra congregación saca al usuario de la lista que se está viendo, así
// que se recarga la tabla y los contadores de miembros.
const moveUserToCongregation = async (user, congregationId) => {
  if (!congregationId || congregationId === selectedCong.value.id) return
  const destino = congregations.value.find((c) => c.id === congregationId)
  const result = await api.put(`/api/admin/users/${user.uid}`, { congregationId })
  if (result?.error) {
    toast.error('No se pudo mover el usuario', result.error)
    return
  }
  await openCongUsers(selectedCong.value)
  await loadCongregations()
  toast.success('Usuario movido', `${user.email} pasó a ${destino?.name || 'otra congregación'}.`)
}

const createUser = async () => {
  if (!newUser.value.email || !newUser.value.password) {
    errorUser.value = 'Usuario y contraseña son requeridos'
    return
  }
  savingUser.value = true
  errorUser.value = null
  try {
    const result = await api.post('/api/admin/users', {
      email: newUser.value.email,
      password: newUser.value.password,
      role: newUser.value.role,
      congregationId: selectedCong.value.id,
    })
    if (result.error) { errorUser.value = result.error; return }
    showCreateUser.value = false
    newUser.value = { email: '', password: '', role: 'member' }
    await openCongUsers(selectedCong.value)
    await loadCongregations()
  } finally {
    savingUser.value = false
  }
}

// ── Modelo de permisos asignado a cada congregación ──────────────────────────
const changeCongregationTemplate = async (cong, templateId) => {
  if (templateId === cong.permissionTemplateId) return
  const previous = cong.permissionTemplateId
  cong.permissionTemplateId = templateId
  const result = await api.put(`/api/admin/congregations/${cong.id}/permission-template`, { templateId })
  if (result?.error) {
    cong.permissionTemplateId = previous
    toast.error('No se pudo asignar el modelo', result.error)
    return
  }
  const tplName = templateOptions.value.find((t) => t.id === templateId)?.name || 'Sin modelo'
  toast.success('Modelo asignado', `${cong.name} ahora usa "${tplName}".`)
}

// ── Modelos de permisos ────────────────────────────────────────────────────────
const loadTemplates = async () => {
  loadingTemplates.value = true
  try {
    const result = await api.get('/api/admin/permission-templates')
    templates.value = result.templates || []
  } finally {
    loadingTemplates.value = false
  }
}

const openCreateTemplate = () => {
  editingTemplate.value = null
  templateForm.value = { name: '', disabledPages: [] }
  errorTemplate.value = null
  showTemplateDialog.value = true
}

const openEditTemplate = (tpl) => {
  editingTemplate.value = tpl
  templateForm.value = { name: tpl.name, disabledPages: [...tpl.disabledPages] }
  errorTemplate.value = null
  showTemplateDialog.value = true
}

const toggleTemplatePage = (key, enabled) => {
  const previous = templateForm.value.disabledPages
  templateForm.value.disabledPages = enabled ? previous.filter((k) => k !== key) : [...previous, key]
}

const saveTemplate = async () => {
  if (!templateForm.value.name.trim()) { errorTemplate.value = 'El nombre es requerido'; return }
  savingTemplate.value = true
  errorTemplate.value = null
  try {
    const payload = { name: templateForm.value.name.trim(), disabledPages: templateForm.value.disabledPages }
    const result = editingTemplate.value
      ? await api.put(`/api/admin/permission-templates/${editingTemplate.value.id}`, payload)
      : await api.post('/api/admin/permission-templates', payload)
    if (result.error) { errorTemplate.value = result.error; return }
    showTemplateDialog.value = false
    await loadTemplates()
  } finally {
    savingTemplate.value = false
  }
}

const setDefaultTemplate = async (tpl) => {
  const result = await api.put(`/api/admin/permission-templates/${tpl.id}/default`)
  if (result?.error) { toast.error('No se pudo marcar como predeterminado', result.error); return }
  await loadTemplates()
}

const confirmDeleteTemplate = (tpl) => {
  confirm.require({
    message: `¿Eliminar el modelo "${tpl.name}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await api.del(`/api/admin/permission-templates/${tpl.id}`)
      await loadTemplates()
    },
  })
}

// ── Enlaces de invitación ─────────────────────────────────────────────────────
const loadInvites = async () => {
  loadingInvites.value = true
  try {
    const result = await getInvites()
    invites.value = result.invites || []
  } finally {
    loadingInvites.value = false
  }
}

const doCreateInvite = async () => {
  creatingInvite.value = true
  try {
    const result = await createInvite(newInviteName.value.trim())
    if (result.error) { toast.error('No se pudo crear el enlace', result.error); return }
    invites.value.unshift(result)
    newInviteName.value = ''
    showCreateInvite.value = false
  } finally {
    creatingInvite.value = false
  }
}

const copyInviteLink = (token, type = 'create') => {
  navigator.clipboard.writeText(getPublicUrl(token, type))
  copiedInviteToken.value = token
  setTimeout(() => { if (copiedInviteToken.value === token) copiedInviteToken.value = null }, 2000)
}

const generateManageInvite = async (cong) => {
  creatingManageInvite.value = true
  try {
    const result = await createInvite(`Gestión — ${cong.name}`, { type: 'manage_users', congregationId: cong.id })
    if (result.error) { toast.error('No se pudo generar el enlace', result.error); return }
    invites.value.unshift(result)
  } finally {
    creatingManageInvite.value = false
  }
}

const confirmRevokeInvite = (invite) => {
  confirm.require({
    message: `¿Revocar el enlace "${invite.name || 'sin nombre'}"? Ya no podrá usarse.`,
    header: 'Confirmar revocación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Revocar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      const result = await revokeInvite(invite.token)
      if (result?.error) { toast.error('No se pudo revocar el enlace', result.error); return }
      invites.value = invites.value.filter((i) => i.token !== invite.token)
    },
  })
}

const openResetPassword = (user) => {
  resetPasswordUser.value = user
  resetPasswordValue.value = ''
  errorResetPassword.value = null
  showResetPassword.value = true
}

const generateRandomPassword = () => {
  resetPasswordValue.value = generatePassword()
}

const copyNewUserPassword = async () => {
  try {
    await navigator.clipboard.writeText(newUser.value.password)
    toast.success('Contraseña copiada')
  } catch {
    toast.error('No se pudo copiar', 'Copiala manualmente desde el campo.')
  }
}

const submitResetPassword = async () => {
  if (!resetPasswordValue.value || resetPasswordValue.value.length < 6) {
    errorResetPassword.value = 'La contraseña debe tener al menos 6 caracteres'
    return
  }
  savingResetPassword.value = true
  errorResetPassword.value = null
  try {
    const result = await api.put(`/api/admin/users/${resetPasswordUser.value.uid}/password`, {
      password: resetPasswordValue.value,
    })
    if (result?.error) { errorResetPassword.value = result.error; return }
    showResetPassword.value = false
    toast.success('Contraseña renovada', `Se actualizó la contraseña de ${resetPasswordUser.value.email}.`)
  } finally {
    savingResetPassword.value = false
  }
}

const confirmDeleteUser = (user) => {
  confirm.require({
    message: `¿Eliminar al usuario "${user.email}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await api.del(`/api/admin/users/${user.uid}`)
      await openCongUsers(selectedCong.value)
      await loadCongregations()
    },
  })
}

onMounted(async () => {
  if (!userStore.isSuperUser) return
  await Promise.all([loadCongregations(), loadInvites(), loadTemplates()])
  const userCongId = userStore.congregationId
  if (userCongId) {
    const cong = congregations.value.find((c) => c.id === userCongId)
    if (cong) await openCongUsers(cong)
  }
})
</script>

<style scoped>
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
  text-align: left;
}

.menu-config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: var(--space-2) var(--space-4);
}

.cong-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-3) var(--space-4);
  max-width: 700px;
}

.invite-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
}
</style>
